import { useMemo, useState } from 'react'
import { CaretSortIcon, CheckIcon } from '@radix-ui/react-icons'
import useSWR from 'swr'
import { fetcher } from '@/hooks'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { PRODUCT_TREE_URL, collectPids, readableCountryCodes, walkGroups } from '@/comm/product-tree'
import { findNodeChain, ROOT_NODE } from './scope-tree'

const TREE_QUERY = { scope: 'roas' }

/** MON-93 roasReadable 裁剪后的产品树（GET /products/tree?scope=roas） */
export function useRoasTree() {
  const { data, error, isLoading } = useSWR([PRODUCT_TREE_URL, TREE_QUERY], fetcher, { revalidateOnFocus: false })
  return { tree: data?.groups ? data : null, error: error?.message, isLoading }
}

function Level({ label, value, options, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={label} className="w-36">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

/** 节点级联：产品线 → 子分组 → 产品；每级首项「全部」= 上一级本身 */
function NodePicker({ groups, node, onChange }) {
  const root = groups.find((g) => g.kind === 'root') ?? { code: ROOT_NODE, children: groups, pids: [] }
  const chain = findNodeChain(groups, node) ?? []
  const [line, sub] = chain
  const deepest = chain.at(-1) ?? null
  const all = (code) => ({ value: code, label: '全部' })
  const subs = line?.children ?? []
  const pids = deepest ? collectPids(deepest) : []
  const names = new Map()
  walkGroups(groups, (g) => (g.pids ?? []).forEach((p) => names.set(String(p.pid), p.name)))
  const pidValue = node.startsWith('pid:') ? node : deepest?.code
  return (
    <>
      <Level
        label="产品线"
        value={line?.code ?? root.code}
        options={[all(root.code), ...(root.children ?? []).map((g) => ({ value: g.code, label: g.name }))]}
        onChange={onChange}
      />
      {subs.length > 0 && (
        <Level
          label="子分组"
          value={sub?.code ?? line.code}
          options={[all(line.code), ...subs.map((g) => ({ value: g.code, label: g.name }))]}
          onChange={onChange}
        />
      )}
      {deepest && pids.length > 0 && (
        <Level
          label="产品"
          value={pidValue}
          options={[all(deepest.code), ...pids.map((pid) => ({ value: `pid:${pid}`, label: names.get(pid) ?? pid }))]}
          onChange={onChange}
        />
      )}
    </>
  )
}

const countriesText = (selected) => {
  if (!selected.length) return '全部国家'
  if (selected.length <= 3) return selected.join(', ')
  return `${selected.length} 国`
}

/** 国家多选：只列可读国家；关闭弹层时提交，空 = 全部国家（含其他国家格子） */
function CountryPicker({ tree, countries, onChange }) {
  const options = useMemo(() => {
    const readable = readableCountryCodes(tree.groups)
    return tree.countries.filter((c) => readable === null || readable.has(c.code))
  }, [tree])
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(countries)
  const toggle = (code) =>
    setDraft((current) => (current.includes(code) ? current.filter((c) => c !== code) : [...current, code]))
  const onOpenChange = (next) => {
    setOpen(next)
    if (next) setDraft(countries)
    else if (draft.join(',') !== countries.join(',')) onChange(draft)
  }
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} aria-label="国家" className="w-40 justify-between">
          <span className="truncate">{countriesText(open ? draft : countries)}</span>
          <CaretSortIcon className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-0">
        <Command>
          <CommandInput placeholder="搜索国家" />
          <CommandList>
            <CommandEmpty>无匹配国家</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={() => setDraft([])}>
                <CheckIcon className={cn('size-4', draft.length ? 'opacity-0' : 'opacity-100')} />
                全部国家
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              {options.map((country) => (
                <CommandItem key={country.code} value={`${country.code} ${country.name}`} onSelect={() => toggle(country.code)}>
                  <CheckIcon className={cn('size-4', draft.includes(country.code) ? 'opacity-100' : 'opacity-0')} />
                  {country.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/** MON-93 页头范围筛选：产品树节点 × 国家；树未到时不渲染 */
export function ScopePicker({ tree, node, countries, onChange }) {
  if (!tree) return null
  return (
    <div className="flex flex-wrap items-center gap-2">
      <NodePicker groups={tree.groups} node={node} onChange={(next) => onChange({ node: next })} />
      <CountryPicker tree={tree} countries={countries} onChange={(next) => onChange({ countries: next })} />
    </div>
  )
}
